import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

/**
 * Button primitive.
 *
 * Thin wrapper around the design system's `.btn` + `.btn--<variant>` classes
 * defined in `src/styles/tailwind.css` (the prototype-derived component layer).
 *
 * Variants and sizes mirror what the design system ships:
 *   variants: default | primary | secondary | tertiary | ghost | destructive | accent | success
 *   sizes:    sm | md | lg-icon | icon
 *
 * Adding a new variant?
 *   1. Add the `.btn--<name>` block in src/styles/tailwind.css.
 *   2. Add `"<name>"` to the ButtonVariant union below.
 *
 * Source of styling truth: src/styles/tailwind.css. Do not author button
 * styling here.
 */
export type ButtonVariant =
  | "default"
  | "primary"
  | "secondary"
  | "tertiary"
  | "ghost"
  | "destructive"
  | "accent"
  | "success"
  /** `success-soft` — green-300 background + green-900 text. Used for the
      "Mark fixed" drawer footer button (Figma node 1:1234). */
  | "success-soft"
  /** `danger-soft` — rose-200 background + rose-900 text. An applied
      Discard (pattern-detail header) — the light-red twin of success-soft. */
  | "danger-soft";

export type ButtonSize = "sm" | "md" | "icon" | "icon-md";

const variantClass: Record<ButtonVariant, string> = {
  default: "",
  primary: "btn--primary",
  secondary: "btn--secondary",
  tertiary: "btn--tertiary",
  ghost: "btn--ghost",
  destructive: "btn--destructive",
  accent: "btn--accent",
  success: "btn--success",
  "success-soft": "btn--success-soft",
  "danger-soft": "btn--danger-soft",
};

const sizeClass: Record<ButtonSize, string> = {
  sm: "btn--sm",
  md: "btn--md",
  icon: "btn--icon",
  "icon-md": "btn--icon-md",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : (type ?? "button")}
        className={cn(
          "btn",
          variantClass[variant],
          size ? sizeClass[size] : undefined,
          className,
        )}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
